import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { Modal } from "react-bootstrap";
import { customStyles } from "../../styles/table-style";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import DataTable from "react-data-table-component";
import BASE_URL from "../../../assets/global/url";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";

const journalColumns = [
  {
    name: "Transaction Date",
    selector: (row) =>
      row.createdAt
        ? format(new Date(row.createdAt), "MMMM do, yyyy 'at' h:mm a")
        : "---",
    sortable: true,
    sortField: "createdAt",
  },
  {
    name: "Total Amount",
    selector: (row) =>
      row.total_amount !== undefined && row.total_amount !== null
        ? formatToTwoDecimal(row?.total_amount)
        : "---",
    sortable: true,
    sortField: "total_amount",
  },
  {
    name: "Payment Type",
    selector: (row) => row.payment_type ?? "---",
    cell: (row) => {
      if (!row.payment_type) return "---";

      const isDebit = row.payment_type === "Debit";
      return (
        <span
          className={`badge py-2 px-3 fs-6 ${
            isDebit
              ? "bg-danger-subtle text-danger border border-danger-subtle"
              : "bg-success-subtle text-success border border-success-subtle"
          }`}
        >
          {row.payment_type}
        </span>
      );
    },
  },
];

const GET_FIXED_ASSET_JOURNAL_URL = `${BASE_URL}/fixedasset/getFixedAssetJournal`;

const FixedAssetJournalModal = ({ show, onHide, fixedAssetId }) => {
  const [searchText, setSearchText] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const debounceTimer = useRef(null);

  const journalPagination = useServerPagination("about:blank", 10, {}, 1);

  useEffect(() => {
    if (show && fixedAssetId) {
      setSearchText("");
      setFilterColumn("all");
      journalPagination.updateApiUrl(GET_FIXED_ASSET_JOURNAL_URL);
      journalPagination.updateParams({
        id: fixedAssetId,
        sortBy: "createdAt",
        sortDirection: "DESC",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, fixedAssetId]);

  // Debounced search handler
  const handleSearchChange = (value) => {
    setSearchText(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      journalPagination.updateParams({
        id: fixedAssetId,
        searchText: value,
        filterColumn,
        sortBy: "createdAt",
        sortDirection: "DESC",
      });
    }, 500);
  };

  // Filter-column dropdown handler
  const handleFilterColumn = (column) => {
    setFilterColumn(column);
    if (searchText) {
      journalPagination.updateParams({
        id: fixedAssetId,
        searchText,
        filterColumn: column,
        sortBy: "createdAt",
        sortDirection: "DESC",
      });
    }
  };

  // Server-side sort handler
  const handleSort = (column, sortDirection) => {
    journalPagination.updateParams({
      id: fixedAssetId,
      searchText,
      filterColumn,
      sortBy: column.sortField,
      sortDirection: sortDirection.toUpperCase(),
    });
  };

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
      dialogClassName="custom-modal-vw90"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold fs-5">
              Fixed Asset Expenses Payment History
            </span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Search filter */}
        <div className="w-100 mb-3">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fa-solid fa-sliders"></i>
              <span className="visually-hidden">Toggle Dropdown</span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button
                  className={`dropdown-item ${filterColumn === "all" ? "active" : ""}`}
                  onClick={() => handleFilterColumn("all")}
                >
                  All
                </button>
              </li>
              <li>
                <button
                  className={`dropdown-item ${filterColumn === "createdAt" ? "active" : ""}`}
                  onClick={() => handleFilterColumn("createdAt")}
                >
                  Transaction Date
                </button>
              </li>
              <li>
                <button
                  className={`dropdown-item ${filterColumn === "total_amount" ? "active" : ""}`}
                  onClick={() => handleFilterColumn("total_amount")}
                >
                  Total Amount
                </button>
              </li>
              <li>
                <button
                  className={`dropdown-item ${filterColumn === "payment_type" ? "active" : ""}`}
                  onClick={() => handleFilterColumn("payment_type")}
                >
                  Payment Type
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Data table */}
        {journalPagination.data?.length > 0 ? (
          <div className="data-table-cell-width">
            <DataTable
              columns={journalColumns}
              data={journalPagination.data}
              customStyles={customStyles}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 16rem)"
              sortServer
              onSort={handleSort}
              className="dataTable"
              defaultSortFieldId="createdAt"
              defaultSortAsc={false}
            />
            <PaginationControls {...journalPagination} />
          </div>
        ) : (
          <div className="alert alert-info">
            <i className="fa-solid fa-info-circle me-2"></i>
            No journal entries found for this fixed asset.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export { FixedAssetJournalModal };
